// ─── Pagina ❤️ Salute — dashboard composizione corporea ─────────────────────
// Dashboard read-only sui dati reali di body_measurements (già in
// window.S.measurements via loadMeasurements). Hero peso+BMI+trend, dettaglio
// 13 valori con delta, insight AI Haiku dai trend, teaser Apple Salute.
// Nessuna migration. Vedi spec docs/superpowers/specs/2026-07-25-health-dashboard-design.md
import { api, HAIKU } from './core.js';
import { loadMeasurements, openMeasurements } from './ui-profile.js';

// 13 metriche: chiave DB, label UI, unità. Ordine = ordine nel dettaglio.
const HEALTH_FIELDS = [
  { key:'peso_kg', lbl:'Peso', unit:'kg' },
  { key:'grasso_pct', lbl:'Grasso corporeo', unit:'%' },
  { key:'massa_magra_kg', lbl:'Massa magra', unit:'kg' },
  { key:'massa_muscolare_kg', lbl:'Massa muscolare', unit:'kg' },
  { key:'muscolo_scheletrico_pct', lbl:'Muscolo scheletrico', unit:'%' },
  { key:'grasso_sottocutaneo_pct', lbl:'Grasso sottocutaneo', unit:'%' },
  { key:'grasso_addominale', lbl:'Grasso addominale', unit:'' },
  { key:'liquidi_pct', lbl:'Liquidi corporei', unit:'%' },
  { key:'proteine_pct', lbl:'Proteine', unit:'%' },
  { key:'bmr_kcal', lbl:'Metabolismo basale', unit:'kcal' },
  { key:'massa_ossea_kg', lbl:'Massa ossea', unit:'kg' },
  { key:'indice_corporeo', lbl:'Indice corporeo', unit:'' },
  { key:'altezza_cm', lbl:'Altezza', unit:'cm' },
];

let hlPeriod = 30; // giorni, default trend

// ── Helpers ──
function fmt(n){ return Number(n).toLocaleString('it-IT', { maximumFractionDigits: 1 }); }
function esc(s){ return String(s).replace(/[&<>]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;'}[c])); }

function bmiOf(m, prof){
  const alt = (m && m.altezza_cm != null) ? m.altezza_cm : (prof && prof.altezza != null ? prof.altezza : null);
  if (!alt || !m || m.peso_kg == null) return null;
  return m.peso_kg / Math.pow(alt/100, 2);
}
function classifyBmi(b){
  if (b < 18.5) return 'Sottopeso';
  if (b < 25)   return 'Normopeso';
  if (b < 30)   return 'Sovrappeso';
  return 'Obesità';
}
// SVG path da array di valori, normalizzato al box w×h (valore alto = in alto).
function sparkPath(values, w, h, pad = 3){
  if (!values.length) return { line:'', fill:'' };
  const min = Math.min(...values), max = Math.max(...values), range = (max-min) || 1;
  const n = values.length, stepX = n>1 ? w/(n-1) : 0;
  const pts = values.map((v,i) => {
    const x = n>1 ? i*stepX : w/2;
    const y = pad + (h-2*pad) * (1 - (v-min)/range);
    return [x, y];
  });
  const line = pts.map((p,i) => `${i?'L':'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  return { line, fill: line + ` L${w},${h} L0,${h} Z` };
}
function deltaStr(cur, prev, unit){
  if (prev == null || cur == null) return '';
  const d = cur - prev;
  if (Math.abs(d) < 0.05) return 'invariato';
  return `${d>0?'▲':'▼'} ${fmt(Math.abs(d))}${unit?' '+unit:''}`;
}
function miniSpark(values){
  const { line } = sparkPath(values, 64, 22, 2);
  return `<svg class="hl-mini" viewBox="0 0 64 22" preserveAspectRatio="none"><path class="hl-mini-line" d="${line}" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

// ── Apertura / chiusura ──
export async function openHealth(){
  try { if (typeof window.logEvent === 'function') window.logEvent('health_open', {}); } catch (_) {}
  document.getElementById('healthModal').classList.add('show');
  await loadMeasurements();
  renderHealthDashboard();
}
export function closeHealth(){ document.getElementById('healthModal').classList.remove('show'); }
export function openHealthAdd(){ closeHealth(); openMeasurements(); }

// ── Navigazione sotto-viste ──
export function showHealthDashboard(){
  const det = document.getElementById('hlDetail'); if (det) det.style.display = 'none';
  const dash = document.getElementById('hlDashboard'); if (dash) dash.style.display = 'block';
  const t = document.getElementById('hlTitle'); if (t) t.textContent = '❤️ Salute';
}
export function showHealthDetail(){
  document.getElementById('hlDashboard').style.display = 'none';
  const d = document.getElementById('hlDetail'); d.style.display = 'block';
  const t = document.getElementById('hlTitle'); if (t) t.textContent = '❤️ Composizione';
  renderHealthDetail();
  if (d.parentElement) d.parentElement.scrollTop = 0;
}

// ── Dashboard ──
export function renderHealthDashboard(){
  showHealthDashboard();
  const meas = window.S.measurements || [];
  const dash = document.getElementById('hlDashboard');
  if (!dash) return;
  if (meas.length === 0){
    dash.innerHTML = `<div class="hl-empty"><div class="hl-empty-ic">❤️</div>
      <p>Nessuna misurazione ancora.<br>Aggiungine una per vedere i tuoi progressi.</p>
      <button class="btn btn-primary" onclick="openHealthAdd()">＋ Aggiungi misurazione</button></div>`;
    return;
  }
  const m = meas[0], prof = window.S.profile || {};
  const bmi = bmiOf(m, prof);
  const bmiHtml = bmi != null
    ? `<div class="hl-bmi"><div class="hl-bmi-lbl">BMI</div><div class="hl-bmi-v">${bmi.toLocaleString('it-IT',{minimumFractionDigits:1,maximumFractionDigits:1})}</div><div class="hl-bmi-cls">${classifyBmi(bmi)}</div></div>`
    : '';
  const stat = (key,lbl,unit) => `<div class="hl-stat"><div class="hl-stat-v">${m[key]!=null?fmt(m[key]):'—'}<small>${m[key]!=null&&unit?' '+unit:''}</small></div><div class="hl-stat-k">${lbl}</div></div>`;
  dash.innerHTML = `
    <div id="hlInsight" class="hl-insight-wrap"></div>
    <div class="hl-seclbl">Composizione corporea<a class="hl-link" onclick="showHealthDetail()">Dettaglio ›</a></div>
    <div class="hl-card hl-tap" onclick="showHealthDetail()">
      <div class="hl-hero-top">
        <div>
          <div class="hl-hero-lbl">Peso attuale</div>
          <div class="hl-weight">${m.peso_kg!=null?fmt(m.peso_kg):'—'}<small>kg</small></div>
          <div class="hl-trend" id="hlTrendPill">—</div>
        </div>
        ${bmiHtml}
      </div>
      <div class="hl-seg" id="hlPeriodSeg" onclick="event.stopPropagation()">
        <button data-days="7" onclick="setHealthPeriod(7)">7 g</button>
        <button data-days="30" onclick="setHealthPeriod(30)">30 g</button>
        <button data-days="90" onclick="setHealthPeriod(90)">90 g</button>
      </div>
      <svg class="hl-spark" width="100%" height="46" viewBox="0 0 360 46" preserveAspectRatio="none">
        <defs><linearGradient id="hlSg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="rgba(29,158,117,.32)"/><stop offset="1" stop-color="rgba(29,158,117,0)"/></linearGradient></defs>
        <path id="hlSparkFill" fill="url(#hlSg)"></path>
        <path id="hlSparkLine" class="hl-spark-line" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
      <div class="hl-stats">
        ${stat('grasso_pct','Grasso','%')}
        ${stat('massa_magra_kg','Massa magra','kg')}
        ${stat('muscolo_scheletrico_pct','Musc. sch.','%')}
      </div>
    </div>
    <div class="hl-card hl-teaser">
      <div class="hl-teaser-badge">Presto</div>
      <div class="hl-teaser-ic">🍎</div>
      <div class="hl-teaser-txt"><b>Collega Apple Salute</b><span>Passi, battito, sonno e allenamenti in automatico.</span></div>
    </div>
    <button class="btn btn-primary hl-addbtn" onclick="openHealthAdd()">＋ Aggiungi misurazione</button>
  `;
  setHealthPeriod(hlPeriod);
  renderHealthInsight();
}

// ── Toggle periodo (aggiorna sparkline + delta) ──
export function setHealthPeriod(days){
  hlPeriod = days;
  const meas = window.S.measurements || [];
  const cutoff = Date.now() - days*86400000;
  const series = meas
    .filter(m => m.peso_kg != null && new Date(m.measured_at).getTime() >= cutoff)
    .slice().sort((a,b) => new Date(a.measured_at) - new Date(b.measured_at));
  const seg = document.getElementById('hlPeriodSeg');
  if (seg) seg.querySelectorAll('button').forEach(b => b.classList.toggle('on', Number(b.dataset.days) === days));
  const values = series.map(m => m.peso_kg);
  const { line, fill } = sparkPath(values, 360, 46);
  const lineEl = document.getElementById('hlSparkLine'), fillEl = document.getElementById('hlSparkFill'), pill = document.getElementById('hlTrendPill');
  if (lineEl) lineEl.setAttribute('d', line);
  if (fillEl) fillEl.setAttribute('d', fill);
  if (pill){
    if (values.length >= 2){
      const d = values[values.length-1] - values[0];
      pill.textContent = `${d<0?'▼':(d>0?'▲':'=')} ${fmt(Math.abs(d))} kg / ${days} giorni`;
    } else {
      pill.textContent = 'Dati insufficienti nel periodo';
    }
  }
}

// ── Dettaglio 13 valori ──
export function renderHealthDetail(){
  const el = document.getElementById('hlDetail'); if (!el) return;
  const meas = window.S.measurements || [];
  const latest = meas[0] || {}, prev = meas[1] || null;
  const rows = HEALTH_FIELDS.filter(f => latest[f.key] != null).map(f => {
    const cur = latest[f.key];
    const dstr = prev ? deltaStr(cur, prev[f.key], f.unit) : '';
    const hist = meas.slice(0,7).filter(mm => mm[f.key] != null).reverse().map(mm => mm[f.key]);
    const sp = hist.length >= 2 ? miniSpark(hist) : '';
    return `<div class="hl-row"><div class="hl-row-l"><div class="hl-row-k">${f.lbl}</div>${dstr?`<div class="hl-delta">${dstr}</div>`:''}</div>${sp}<div class="hl-row-v">${fmt(cur)}<small>${f.unit?' '+f.unit:''}</small></div></div>`;
  }).join('');
  const dateStr = latest.measured_at ? new Date(latest.measured_at).toLocaleDateString('it-IT',{day:'numeric',month:'short'}) : '—';
  el.innerHTML = `
    <button class="hl-back" onclick="showHealthDashboard()">‹ Salute</button>
    <div class="hl-detail-sub">Ultima misurazione · ${dateStr}${prev?' · variazioni vs precedente':''}</div>
    <div class="hl-card">${rows || '<p class="hl-muted">Nessun valore disponibile.</p>'}</div>
  `;
}

// ── Insight AI (Haiku) ──
const HL_INSIGHT_SYSTEM = 'Sei un coach fitness AI. Analizza i trend di composizione corporea e dai un feedback breve, concreto e motivante. Italiano informale, prima persona anonima (mai nomi propri). Massimo 2 frasi. Nessun disclaimer medico, nessuna diagnosi.';
function insightCard(text){ return `<div class="hl-insight"><span class="hl-insight-ic">💡</span><p>${esc(text)}</p></div>`; }
export async function renderHealthInsight(){
  const el = document.getElementById('hlInsight'); if (!el) return;
  const meas = window.S.measurements || [];
  if (meas.length < 2){
    el.innerHTML = `<div class="hl-insight hl-insight-muted"><span class="hl-insight-ic">💡</span><p>Aggiungi almeno 2 misurazioni per ricevere un'analisi dei tuoi progressi.</p></div>`;
    return;
  }
  if (window.S._healthInsight){ el.innerHTML = insightCard(window.S._healthInsight); return; }
  el.innerHTML = `<div class="hl-insight hl-insight-muted"><span class="hl-insight-ic">💡</span><p>Sto analizzando i tuoi trend…</p></div>`;
  const recent = meas.slice(0,5).map(m => {
    const dt = m.measured_at ? new Date(m.measured_at).toLocaleDateString('it-IT',{day:'2-digit',month:'2-digit'}) : '?';
    const parts = [];
    if (m.peso_kg != null) parts.push(`peso ${fmt(m.peso_kg)}kg`);
    if (m.grasso_pct != null) parts.push(`grasso ${fmt(m.grasso_pct)}%`);
    if (m.massa_magra_kg != null) parts.push(`magra ${fmt(m.massa_magra_kg)}kg`);
    return `${dt}: ${parts.join(', ')}`;
  }).join('\n');
  const obiettivo = (window.S.profile && window.S.profile.obiettivo) ? `Obiettivo: ${window.S.profile.obiettivo}.` : '';
  const prompt = `Misurazioni recenti (dalla più recente):\n${recent}\n${obiettivo}\nIn 1-2 frasi: cosa dicono questi trend e un consiglio pratico.`;
  try {
    const text = await api([{ role:'user', content: prompt }], HL_INSIGHT_SYSTEM, 220, HAIKU);
    window.S._healthInsight = (text || '').trim();
    el.innerHTML = window.S._healthInsight ? insightCard(window.S._healthInsight) : '';
  } catch (_) {
    el.innerHTML = ''; // errore: nasconde la card, non blocca la dashboard
  }
}
